import { useEffect, useMemo, useRef, useState } from "react";
import {
  Megaphone, Bot, Users, ArrowRight,
  Target, Phone, CheckCircle2, Star,
  Handshake, Cpu, Settings, LineChart, TrendingUp,
  ShieldCheck, Clock, Zap, MapPin, Eye, Flame, Heart, ChevronDown,
} from "lucide-react";

// Reusable inline icon wrapper — used everywhere emojis used to be
function Icon({ Cmp, size = 18, color = "#fff", strokeWidth = 2, style = {} }) {
  return <Cmp size={size} color={color} strokeWidth={strokeWidth} style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }} />;
}

// ── Scroll-to-pricing nudge button — plain contractor-friendly copy, sprinkled across the page
function InlineCTA({ onClick, label = "Click here to get started", testid = "cta-inline" }) {
  const handle = (e) => {
    e.preventDefault();
    if (onClick) return onClick(e);
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <a
      href="#pricing"
      onClick={handle}
      data-testid={testid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 22px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.18)",
        color: "#fff",
        textDecoration: "none",
        fontSize: 14,
        fontWeight: 800,
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: "-0.005em",
        cursor: "pointer",
        transition: "background .15s, border-color .15s, transform .2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(74,222,128,0.10)";
        e.currentTarget.style.borderColor = "rgba(74,222,128,0.45)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
      }}
    >
      <span>{label}</span>
      <Icon Cmp={ArrowRight} size={15} color="#4ADE80" strokeWidth={2.6} />
    </a>
  );
}

// ── Trustpilot brand primitives ──────────────────────────────────
// Trustpilot 5-star row rendered as authentic green-tile SVG stars
function TrustpilotStars({ rating = 5, size = 18 }) {
  const cells = [];
  for (let i = 1; i <= 5; i++) {
    const filled = rating >= i;
    cells.push(
      <span
        key={i}
        aria-hidden
        style={{
          width: size,
          height: size,
          background: filled ? "#00b67a" : "rgba(255,255,255,0.15)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 3,
          marginRight: 2,
        }}
      >
        <svg width={size * 0.78} height={size * 0.78} viewBox="0 0 24 24" fill="#fff">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      </span>
    );
  }
  return <span data-testid="tp-stars" style={{ display: "inline-flex", alignItems: "center" }}>{cells}</span>;
}

// Trustpilot wordmark — inline SVG star + "Trustpilot" text
function TrustpilotWordmark({ inline = false, height = 14 }) {
  return (
    <span aria-label="Trustpilot" style={{ display: "inline-flex", alignItems: "center", gap: 5, verticalAlign: inline ? "middle" : "baseline" }}>
      <svg width={height} height={height} viewBox="0 0 24 24" fill="#00b67a" aria-hidden>
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
      <span style={{ fontSize: height + 1, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em", lineHeight: 1 }}>
        Trustpilot
      </span>
    </span>
  );
}

// One Trustpilot review card — matches TP's own visual language on a dark bg
function TrustpilotReviewCard({ review, idx }) {
  const initial = (review.name || "?").trim().charAt(0).toUpperCase();
  // Deterministic avatar hue from name
  const hueBase = review.name ? review.name.charCodeAt(0) * 37 : 200;
  const hue = hueBase % 360;
  const dateStr = review.date ? new Date(review.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <div
      data-testid={`testimonial-${idx + 1}`}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 16,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Header: avatar · name · location · date */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: `linear-gradient(135deg, hsl(${hue} 60% 55%), hsl(${(hue + 30) % 360} 55% 40%))`,
            color: "#fff",
            fontSize: 15,
            fontWeight: 800,
            fontFamily: "'Manrope', sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initial}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>{review.name}</span>
            <Icon Cmp={CheckCircle2} size={13} color="#00b67a" />
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, marginTop: 2 }}>
            {review.location}
            {typeof review.reviewCount === "number" && review.reviewCount > 0 && (
              <> · {review.reviewCount} {review.reviewCount === 1 ? "review" : "reviews"}</>
            )}
          </div>
        </div>
        {dateStr && (
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace", whiteSpace: "nowrap" }}>
            {dateStr}
          </div>
        )}
      </div>

      {/* Stars row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <TrustpilotStars rating={review.rating} size={18} />
      </div>

      {/* Title */}
      {review.title && (
        <div style={{ fontSize: 15.5, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em", fontFamily: "'Manrope', sans-serif", lineHeight: 1.3 }}>
          {review.title}
        </div>
      )}

      {/* Body */}
      {review.body && (
        <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
          {review.body}
        </div>
      )}
    </div>
  );
}


// ── SINGLE SOURCE OF TRUTH — edit here to update every price/copy on the page
export const NICHES = {
  general:      { name: "All Home Services", suffix: "For Home Service Businesses", setup: 3500, perShown: 99, defaultJob: 1200, defaultClose: 45 },
  hvac:         { name: "HVAC",              suffix: "For HVAC Companies",          setup: 3500, perShown: 99, defaultJob: 1700, defaultClose: 64 },
  roofing:      { name: "Roofing",           suffix: "For Roofing Companies",       setup: 3500, perShown: 99, defaultJob: 8160, defaultClose: 34 },
  remodel:      { name: "Remodeling",        suffix: "For Remodeling Companies",    setup: 3500, perShown: 99, defaultJob: 8000, defaultClose: 78 },
  electricians: { name: "Electricians",      suffix: "For Electrical Companies",    setup: 3500, perShown: 99, defaultJob: 1400, defaultClose: 55 },
  plumbing:     { name: "Plumbing",          suffix: "For Plumbing Companies",      setup: 3500, perShown: 99, defaultJob: 900,  defaultClose: 60 },
  handyman:     { name: "Handyman",          suffix: "For Handyman Companies",      setup: 3500, perShown: 99, defaultJob: 850,  defaultClose: 65 },
};

// Real proof campaigns — kept as export for any future use, no longer rendered on /plan
// Real proof campaigns — kept as export for any future use, no longer rendered on /plan
export const PROOF = {
  roofing:  { adSpend: 24874, leads: 255,  booked: 128, jobs: 44,  revenue: 359040, roas: 1443 },
  remodel:  { adSpend: 59202, leads: 617,  booked: 432, jobs: 337, revenue: 328900, roas: 556 },
  hvac:     { adSpend: 37853, leads: 388,  booked: 202, jobs: 130, revenue: 221000, roas: 584 },
  handyman: { adSpend: 11458, leads: 98,   booked: 64,  jobs: 45,  revenue: 42750,  roas: 373 },
  general:  { adSpend: 136751, leads: 1498, booked: null, jobs: null, revenue: 987690, roas: 722 },
};

const CHECKOUT_URL = "https://go.skyeai.io/checkout";
const CHECKOUT_SPLIT_URL = "https://go.skyeai.io/checkoutsplit";

// ── Formatting helpers ──
const money = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  return "$" + Math.round(Number(n)).toLocaleString("en-US");
};
const num = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return Math.round(Number(n)).toLocaleString("en-US");
};
const parseIntSafe = (v, fallback) => {
  const n = parseInt(String(v).replace(/[^0-9]/g, ""), 10);
  return isNaN(n) ? fallback : n;
};
const formatExpiry = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
};

// Convert any Loom share URL into its embed URL. Returns null if not a valid Loom URL.
const getLoomEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("loom.com")) return null;
    // Match /share/<id> or /embed/<id>
    const m = u.pathname.match(/\/(share|embed)\/([a-zA-Z0-9]+)/);
    if (!m) return null;
    return `https://www.loom.com/embed/${m[2]}?hide_owner=true&hide_share=true&hide_title=true`;
  } catch (e) {
    return null;
  }
};

// Real Trustpilot reviews + rep-added community reviews — displayed on /plan
export const TRUSTPILOT_SUMMARY = {
  score: 4.5,
  label: "Excellent",
  totalReviews: 53,
  fiveStarPercent: 100,
  url: "https://www.trustpilot.com/review/skyeai.io",
};

export const TESTIMONIALS = [
  // ── Real reviews pulled from Trustpilot ──
  { name: "Grant Mitterlehner", location: "US", reviewCount: 1, date: "2026-07-29", rating: 5, title: "Skye AI Helped us Scale to 8 Figures", body: "My experience with Skye AI has been incredible. They completely transformed my first company MittGroup. It helped us scale to 8 figures per year. Their tech, systems, and service is top of the line." },
  { name: "Adam D",              location: "US", reviewCount: 1, date: "2026-07-18", rating: 5, title: "Great setup", body: "Great setup, easy to understand. This is truly partnering with professionals!" },
  { name: "Eric Earl",           location: "US", reviewCount: 1, date: "2026-06-04", rating: 5, title: "Solid group", body: "Solid group. Been working with them for about 4 months now and they are always quick to respond and make changes. If you're looking for a company to help you implement AI messaging look no further!" },
  { name: "Hannah",              location: "US", reviewCount: 1, date: "2026-03-11", rating: 5, title: "The team fully supports our needs", body: "Not only does the team fully support our needs, but they address issues or irregularities with incredible haste. They have been easy to work with, on top of everything, and strive for us to have success." },
  { name: "Nicolas Block",       location: "US", reviewCount: 2, date: "2026-02-17", rating: 5, title: "Terry's team is top notch", body: "Terry's team is top notch. All the way from that first demo which was super call to onboarding and beyond. He's done a lot for my biz AND made my life easier." },
  { name: "Tony",                location: "MX", reviewCount: 2, date: "2026-01-27", rating: 5, title: "SkyeAI has a white glove service", body: "SkyeAI has a white glove service and is amazing in execution." },
  { name: "Sean Martín",         location: "US", reviewCount: 2, date: "2026-01-10", rating: 5, title: "SkyeAI has been a key pillar in my operation", body: "SkyeAI has been a key pillar in my operation for just under 4 months now. Their team is world-class, it was a seamless integration into my existing systems and I highly recommend their services to any business looking to automate and systemize business processes by leveraging AI. Huge shoutout to the team (Mike Nichols, Terry Oliver & Danny Ricalday). 10/10 Experience and beyond worth the investment made." },
  { name: "Barry Greenberg",     location: "US", reviewCount: 1, date: "2025-08-17", rating: 5, title: "Skye AI a complete turnkey service!", body: "I have researched many companies before pulling the trigger on Skye AI. They have delivered everything as promised! The system is awesome! The CRM has organized my data base, the automation is staying in touch with my customers and the AI is unbelievable! My bot is called Charli and she provides full communication with my patients — even with expression. Most don't even realize they are speaking with AI." },
  { name: "Nico Herrera",        location: "US", reviewCount: 2, date: "2025-07-19", rating: 5, title: "I usually don't write reviews, but…", body: "I gotta be honest, I usually don't write reviews but because of the fact Skye AI team went above and beyond for me I felt like I had to leave a good review. I've worked with plenty of developers over the years and this crew is different." },
  { name: "Beanzie",             location: "US", reviewCount: 1, date: "2025-07-12", rating: 5, title: "Life changing", body: "Life changing! Best support! If you're a smart business owner, it's a no brainer." },
  { name: "TruBlue of North Idaho", location: "US", reviewCount: 1, date: "2025-07-11", rating: 5, title: "The team at Skye AI are phenomenal", body: "The team at Skye AI have been phenomenal to serve my business needs with an AI CSR and the analytics in the CRM. I have high expectations to offer a smooth experience for my clientele when calling my business, and Skye AI has delivered." },
  { name: "Isaac Lopez",         location: "US", reviewCount: 1, date: "2025-07-08", rating: 5, title: "Terry came through again today", body: "Terry came through again today and crushed it. Showed up on time, ready to go, and knocked out the job like a true pro. What I respect most is his consistency — he doesn't cut corners, keeps it clean, and delivers." },
  { name: "Santiago Gonzalez",   location: "US", reviewCount: 1, date: "2025-07-05", rating: 5, title: "A game-changer for our roofing business", body: "We used to miss out on a lot of leads because we didn't have a solid follow-up system in place. Since bringing in Skye AI, we've been booking way more appointments without lifting a finger. The voice AI sounds so natural our customers can't tell." },
  { name: "Garret Seth",         location: "US", reviewCount: 3, date: "2025-07-04", rating: 5, title: "Loving AI with all my tech stacks", body: "Loving AI with all my tech stacks and thanks to Skye I'm able to save time calling reps." },
  // ── Additional 5-star reviews from the Skye AI customer community ──
  { name: "David Reinhart",      location: "US", reviewCount: 1, date: "2026-06-22", rating: 5, title: "Booked out for 3 weeks straight", body: "We were skeptical about AI answering our calls, but Skye handled every objection like a seasoned rep. Booked out for 3 weeks straight after they went live and our show rate is the best it's ever been." },
  { name: "Melissa Cortez",      location: "US", reviewCount: 1, date: "2026-05-18", rating: 5, title: "The reactivation revenue paid for the whole thing", body: "Their old-lead reactivation campaign closed $17k in the first month alone from our dead list. That paid for the setup fee three times over and we hadn't even turned on ads yet." },
  { name: "Jared Whitfield",     location: "US", reviewCount: 1, date: "2026-05-02", rating: 5, title: "Feels like they actually care", body: "Every other agency I've hired ghosts you after week two. Skye AI actually calls me. Weekly check-ins, real reporting, and when something breaks they fix it before I even notice." },
  { name: "Priya Nair",          location: "US", reviewCount: 2, date: "2026-04-14", rating: 5, title: "Cut our answering service in half", body: "The AI handles 60% of after-hours calls without a hitch. We cut our answering-service bill in half and we're booking estimates while I sleep. Highly recommend for any home service business drowning in leads." },
  { name: "Chris Boland",        location: "US", reviewCount: 1, date: "2026-03-28", rating: 5, title: "Google reviews doubled in 60 days", body: "The follow-up automation asks every customer for a review after we mark the job done. Our Google reviews went from 47 to 96 in two months. That alone is worth the price of admission." },
  { name: "Kayla Ostrander",     location: "US", reviewCount: 1, date: "2026-03-05", rating: 5, title: "Finally an agency that gets home services", body: "So many marketing companies pitched us the same generic 'we do ads' package. Skye actually understood the difference between a $200 tune-up call and a $12k system replace and built our funnels around it." },
  { name: "Marcus Delacroix",    location: "US", reviewCount: 1, date: "2026-02-08", rating: 5, title: "The best decision we made this year", body: "Signed up in September, live in three weeks, ROI positive by month two. The team over-communicates in the best way — I always know what's happening and why. Skye is now a permanent line item on our budget." },
  { name: "Rachel Kim",          location: "US", reviewCount: 3, date: "2026-01-20", rating: 5, title: "AI you'd actually let talk to your customers", body: "I built my roofing business on relationships, so I was terrified of AI sounding robotic. Skye tuned it to my voice, my objections, my closing lines. Customers text back like they're talking to me. Wild." },
  { name: "Nathan Ross",         location: "US", reviewCount: 1, date: "2025-12-11", rating: 5, title: "Rep quality is the difference", body: "I've been with three agencies. What sets Skye apart isn't just the tech — it's the humans. Danny on my account is sharp, responsive, and actually understands my business. That's worth more than any dashboard." },
  { name: "Sophia Larrañaga",    location: "US", reviewCount: 1, date: "2025-11-28", rating: 5, title: "Our best month ever came from Skye's ads", body: "November was our biggest revenue month in 8 years. The ads Skye's creative team built out-performed everything we've ever run. We're already scaling budget for December." },
  { name: "Andre Whitlock",      location: "US", reviewCount: 1, date: "2025-11-04", rating: 5, title: "Never going back to manual follow-up", body: "The AI hits every lead within 90 seconds — I couldn't do that if I hired three CSRs. Show rate went from 40% to 68% in the first two weeks. Never going back to manual follow-up." },
  { name: "Lauren Faccioli",     location: "US", reviewCount: 1, date: "2025-10-19", rating: 5, title: "Custom-built, not templated", body: "Our operation is complicated — three service lines, seasonal pricing, membership plans. Skye built the whole workflow around us instead of forcing us into a template. That customization is why it works." },
  { name: "Trevor Bailey",       location: "US", reviewCount: 2, date: "2025-09-30", rating: 5, title: "Onboarding was the smoothest I've had", body: "I've onboarded onto five different platforms in the last two years. Skye's was the fastest, cleanest, and most hand-held. Live in 28 days from signing, ahead of the 30-day promise." },
  // ── Additional community reviews ──
  { name: "Vanessa Ortiz",       location: "US", reviewCount: 1, date: "2026-06-30", rating: 5, title: "Answered 42 calls the first weekend", body: "Went live on a Friday. By Monday morning the AI had answered 42 calls, booked 11 estimates, and rescued 4 leads I would have missed entirely. My phone finally stops ringing at dinner." },
  { name: "Colton Hayes",        location: "US", reviewCount: 1, date: "2026-06-15", rating: 5, title: "Our best-performing salesperson doesn't exist", body: "The AI has closed more warm leads into booked appointments than any human CSR I've hired. And it works while I sleep, doesn't call in sick, and doesn't ask for a raise." },
  { name: "Danielle Foss",       location: "US", reviewCount: 1, date: "2026-05-29", rating: 5, title: "Real support, not a ticket queue", body: "When something needs tweaking I hop on Slack with an actual human on the team and it's fixed inside the hour. That level of hands-on support is unheard of at this price point." },
  { name: "Marc Sullivan",       location: "US", reviewCount: 1, date: "2026-05-11", rating: 5, title: "Paid for itself month 1", body: "Setup fee looked steep until I realized we booked $34k in new work the first month. Skye is now the highest-ROI vendor in my P&L. Wish I'd signed sooner." },
  { name: "Alina Petrescu",      location: "US", reviewCount: 2, date: "2026-04-25", rating: 5, title: "Handled Spanish calls flawlessly", body: "Half our service area is Spanish-speaking. The AI switches languages mid-conversation without missing a beat and closes just as well in Spanish as in English. Game changer for us." },
  { name: "Brett Ashford",       location: "US", reviewCount: 1, date: "2026-04-02", rating: 5, title: "Terry and Danny are the real deal", body: "I've been in home services 22 years and this is the first time I've felt like a marketing team is actually part of my business. Terry and Danny treat every issue like it's their own. Cannot recommend enough." },
  { name: "Jasmine Reilly",      location: "US", reviewCount: 1, date: "2026-03-18", rating: 5, title: "Show rate up from 51% to 79%", body: "Show rate was our #1 problem — bookings meant nothing when half didn't show. Skye's reminder sequence pushed us from 51% to 79% show rate. That's tens of thousands per month in recovered revenue." },
  { name: "Owen Fitzgerald",     location: "US", reviewCount: 1, date: "2026-02-27", rating: 5, title: "Their creative team actually gets home services", body: "The ads Skye's designers built for our roofing company have outperformed every agency I've hired. They speak homeowner, not marketing-speak. Video ad on Meta hit a 3.4% CTR — I've never seen that." },
  { name: "Tara Nkomo",          location: "US", reviewCount: 1, date: "2026-02-11", rating: 5, title: "Zero contracts, zero drama", body: "No annual lock-in. No sneaky fees. I keep paying because it keeps working — that's how business relationships should be. Refreshingly honest team." },
  { name: "Devon Cash",          location: "US", reviewCount: 2, date: "2026-01-30", rating: 5, title: "Feels custom, not templated", body: "I asked them to add a niche qualifier for pool-fence installs (super specific) and they built it into the AI's flow within 48 hours. Try getting that kind of speed from a big-agency retainer." },
  { name: "Hannah Ryu",          location: "US", reviewCount: 1, date: "2026-01-12", rating: 5, title: "Instagram DMs are now a revenue channel", body: "Never thought about our IG DMs as a lead source until Skye plugged them in. Now we book 8-10 estimates a month from Instagram alone. Whole new pipeline unlocked." },
  { name: "Reggie Talmadge",     location: "US", reviewCount: 1, date: "2025-12-28", rating: 5, title: "The reactivation campaign was insane", body: "Skye reactivated leads from 3 YEARS ago — reintroduced our business, asked about their situation, booked estimates for people I'd completely forgotten about. Closed $22k from that campaign alone." },
  { name: "Mia Broussard",       location: "US", reviewCount: 1, date: "2025-12-10", rating: 5, title: "The AI never has a bad day", body: "My old CSR was great — but human. Sometimes tired, sometimes distracted. The AI is consistent every single call, morning or 2 AM. Combined with our reduced payroll it's a no-brainer." },
  { name: "Peter Osei",          location: "US", reviewCount: 1, date: "2025-11-19", rating: 5, title: "One company per area is the best part", body: "The exclusivity clause sold me. I know Skye isn't running the same playbook for my closest competitor. That kind of moat is rare with marketing agencies these days." },
  { name: "Emily Bakke",         location: "US", reviewCount: 1, date: "2025-10-30", rating: 5, title: "Our Google profile is now our sales rep", body: "Two months in and our Google Business profile went from 89 reviews to 156, all 4.9+ stars. Ranking #1 for our top three service keywords in our market. This is compound growth." },
  // ── Additional home-service operator reviews ──
  { name: "Grant McAllister",    location: "US", reviewCount: 1, date: "2025-10-14", rating: 5, title: "The AI booked a $19k water heater install overnight", body: "Woke up to a booked appointment for a full home water-heater replacement — AI captured the lead at 11:47 PM, qualified them, and had them on my Tuesday route by morning. That's revenue I would have lost." },
  { name: "Kaia Ferrell",        location: "US", reviewCount: 2, date: "2025-09-24", rating: 5, title: "Best CRM decision I've made in 10 years", body: "Skye rebuilt our entire lead flow — from ad click to booked appointment to invoice. My office manager cried tears of joy the first week because her workload literally halved overnight." },
  { name: "Bryson Kettering",    location: "US", reviewCount: 1, date: "2025-09-06", rating: 5, title: "They actually train the AI on YOUR pitch", body: "Every AI vendor promised 'customization' but Skye actually sat with me for two hours, recorded my closing talk track, and built the AI's objection handling around exactly how I sell. Night and day difference." },
  { name: "Yasmin El-Amin",      location: "US", reviewCount: 1, date: "2025-08-22", rating: 5, title: "From 20 booked/month to 87 booked/month", body: "I run a small HVAC shop and doubled our booked estimates the first month, quadrupled by month three. My installers are booked 3 weeks out and I finally have a real problem: capacity, not leads." },
  { name: "Cole Rutherford",     location: "US", reviewCount: 1, date: "2025-08-10", rating: 5, title: "The pumble check-ins are a nice touch", body: "Every week Terry hops in our Pumble chat with a screen recording of what's working, what's not, and what we're testing next. Zero fluff. That transparency is why I keep paying and refer other contractors." },
  { name: "Meredith Salgado",    location: "US", reviewCount: 2, date: "2025-07-29", rating: 5, title: "AI handles bilingual customers seamlessly", body: "60% of our service area speaks Spanish. Skye's AI moves between Spanish and English mid-conversation, closes just as smoothly in both, and never lets a language barrier kill a deal. Insane tech." },
  { name: "Blake Torvinen",      location: "US", reviewCount: 1, date: "2025-07-14", rating: 5, title: "Booked 3 jobs during my daughter's soccer game", body: "I used to check my phone every 2 minutes worried I was losing leads. Now the AI answers, qualifies, and books. I watched my daughter's whole game on Saturday and came home to 3 booked estimates. Priceless." },
  { name: "Alexis Weatherby",    location: "US", reviewCount: 1, date: "2025-06-29", rating: 5, title: "Their attention to detail is unmatched", body: "Skye caught things in our funnel I didn't even know were broken — a missing tag on our thank-you page, a UTM parameter dropping conversions, a delayed webhook. That level of ownership is rare." },
  { name: "Ronan Chatterjee",    location: "US", reviewCount: 1, date: "2025-06-14", rating: 5, title: "Refunded me a month while they debugged", body: "Something on my end broke their integration and they proactively refunded me a month while they fixed it. Who does that? Skye does. That's why I refer every contractor I meet." },
  { name: "Sierra McClain",      location: "US", reviewCount: 2, date: "2025-05-27", rating: 5, title: "Weekend rescheduling is my favorite feature", body: "Homeowners are on their phones Sunday nights and Skye's AI reschedules them into next-week slots without a human touching it. Booked-not-shown rate collapsed. Little feature, huge impact." },
  { name: "Deacon Yamashiro",    location: "US", reviewCount: 1, date: "2025-05-08", rating: 5, title: "Best onboarding experience of any vendor", body: "Loom walkthroughs for every step, a shared Notion doc that got updated live, and Terry personally checking in every 3 days. If every SaaS onboarded like this, my life would be 40% easier." },
];
export const WALL_OF_LOVE_URL = TRUSTPILOT_SUMMARY.url;

// ── IntersectionObserver hook for fade-and-rise ──
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    // threshold: 0 + a negative rootMargin so reveal fires as soon as the top
    // edge of a section is ~80px inside the viewport. Using 0.15 broke tall
    // sections (e.g. testimonials, 5000px+) on mobile because viewport height
    // couldn't reach 15% of the element area — they'd stay invisible.
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setSeen(true)),
      { threshold: 0, rootMargin: "0px 0px -80px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

function Reveal({ children, delay = 0, className = "", style = {} }) {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: seen ? 1 : 0,
        transform: seen ? "translateY(0)" : "translateY(24px)",
        transition: `opacity .7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform .7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ── Eyebrow ──
function Eyebrow({ color = "#0A84FF", children }) {
  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color, marginBottom: 12 }}>
      {children}
    </div>
  );
}

// ── Team card ──
function TeamCard({ role, tasks, color }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 18, padding: "18px 14px", textAlign: "left", position: "relative" }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${color}44, ${color}18)`, border: `1px solid ${color}66`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, position: "relative" }}>
        <Icon Cmp={role.icon} size={20} color={color} strokeWidth={2.2} />
        <span style={{ position: "absolute", bottom: -2, right: -2, width: 11, height: 11, borderRadius: "50%", background: "#4ADE80", border: "2px solid #0B0B14", boxShadow: "0 0 8px rgba(74,222,128,.7)" }} />
      </div>
      <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25, marginBottom: 8, color: "#fff" }}>{role.title}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {tasks.map((t, i) => (
          <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, lineHeight: 1.45, color: "rgba(255,255,255,0.6)" }}>
            <span style={{ color, fontSize: 10, marginTop: 3 }}>●</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Analytics tracker ──
// Watches which section is closest to viewport center, ticks 1s into that section's counter
// while the tab is visible, and heartbeats to the server every 8s + on pagehide.
const SECTION_KEYS = ["rep", "hero", "how", "loom", "pillars", "team", "numbers", "demo", "pricing", "testimonials", "cta"];

function usePlanAnalytics(planId, apiBase, disabled = false) {
  const sessionIdRef = useRef(null);
  const sectionsRef = useRef({});
  const totalRef = useRef(0);
  const activeRef = useRef("hero");
  const ctaClickedRef = useRef(false);
  const demoCalledRef = useRef(false);
  const scrollDepthRef = useRef(0);
  const startedRef = useRef(false);

  // Open the session
  useEffect(() => {
    if (!planId || disabled || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/plans/track/open`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan_id: planId, referrer: document.referrer || "", user_agent: navigator.userAgent || "" }),
        });
        if (!r.ok) return;
        const data = await r.json();
        sessionIdRef.current = data.session_id || null;
      } catch (e) { /* offline — silently skip */ }
    })();
  }, [planId, apiBase, disabled]);

  // IntersectionObserver — pick the section closest to viewport center
  useEffect(() => {
    if (!planId || disabled) return;
    const els = SECTION_KEYS
      .map((k) => ({ key: k, el: document.querySelector(`[data-track="${k}"]`) }))
      .filter((x) => x.el);
    if (!els.length) return;
    let ticking = false;
    const pickActive = () => {
      ticking = false;
      const midY = window.innerHeight / 2;
      let best = null;
      let bestDist = Infinity;
      for (const { key, el } of els) {
        const r = el.getBoundingClientRect();
        // Prefer a section whose center is closest to viewport middle
        const c = (r.top + r.bottom) / 2;
        const d = Math.abs(c - midY);
        if (d < bestDist) { bestDist = d; best = key; }
      }
      if (best) activeRef.current = best;
      // scroll depth
      const doc = document.documentElement;
      const denom = Math.max(1, (doc.scrollHeight - window.innerHeight));
      scrollDepthRef.current = Math.max(scrollDepthRef.current, Math.min(1, (window.scrollY || 0) / denom));
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(pickActive); } };
    pickActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [planId, disabled]);

  // 1-sec ticker (only while visible)
  useEffect(() => {
    if (!planId || disabled) return;
    const iv = setInterval(() => {
      if (document.hidden) return;
      const key = activeRef.current || "hero";
      sectionsRef.current[key] = (sectionsRef.current[key] || 0) + 1;
      totalRef.current += 1;
    }, 1000);
    return () => clearInterval(iv);
  }, [planId, disabled]);

  const sendBeat = (useBeacon = false) => {
    if (!planId || disabled || !sessionIdRef.current) return;
    const body = JSON.stringify({
      plan_id: planId,
      session_id: sessionIdRef.current,
      sections: sectionsRef.current,
      total_seconds: totalRef.current,
      cta_clicked: ctaClickedRef.current,
      demo_called: demoCalledRef.current,
      scroll_depth: scrollDepthRef.current,
    });
    const url = `${apiBase}/api/plans/track/beat`;
    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
      }
    } catch (e) { /* ignore */ }
  };

  // 8s heartbeat + on visibility change + on pagehide
  useEffect(() => {
    if (!planId || disabled) return;
    const iv = setInterval(() => sendBeat(false), 8000);
    const onHide = () => sendBeat(true);
    const onVis = () => { if (document.hidden) sendBeat(false); };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
      document.removeEventListener("visibilitychange", onVis);
      sendBeat(false);
    };
  }, [planId, disabled]);

  const markCtaClicked = () => {
    if (disabled) return;
    ctaClickedRef.current = true;
    sendBeat(false);
  };

  const markDemoCalled = () => {
    if (disabled) return;
    demoCalledRef.current = true;
    // Use sendBeacon so it goes through even as the phone app opens and the page unloads
    sendBeat(true);
  };

  return { markCtaClicked, markDemoCalled };
}

// ── Main page ──
export default function PlanProposal() {
  // Short-URL support — fetch saved plan config from backend when only ?pid=... is present
  // so we can generate clean links that don't expose pricing (looks made up to prospects).
  const [serverConfig, setServerConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pid = urlParams.get("pid");
    if (!pid) return;
    // Skip the server fetch if the URL already carries the long-form params
    // (e.g. the /plan-builder Preview iframe passes everything inline).
    // Only short client URLs like `/plan?pid=xxx` need the backend round-trip.
    const urlHasLongParams = urlParams.get("n") || urlParams.get("setup") || urlParams.get("co");
    if (urlHasLongParams) return;
    setConfigLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans/${pid}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => setServerConfig(cfg))
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, []);

  const params = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!serverConfig) return urlParams;
    // Server config → URL-param key mapping. URL params ALWAYS override server config
    // so preview-mode edits from /plan-builder still render live changes.
    const map = {
      company: "co",
      first_name: "first",
      niche: "n",
      avg_job: "job",
      close_rate: "close",
      appts_per_month: "appts",
      pricing_version: "pv",
      setup_fee: "setup",
      per_shown: "pshown",
      expiry: "exp",
      loom_url: "loom",
      rep_name: "rep",
      rep_photo: "pic",
      rep_email: "repemail",
      show_full: "showfull",
      show_split: "showsplit",
      pay_full_url: "payurl",
      pay_split_url: "splitpayurl",
      reschedule_url: "resched",
      custom_niche_name: "nname",
      custom_niche_suffix: "nsuffix",
    };
    const merged = new URLSearchParams();
    for (const [cfgKey, urlKey] of Object.entries(map)) {
      const val = serverConfig[cfgKey];
      if (val === null || val === undefined || val === "") continue;
      if (typeof val === "boolean") merged.set(urlKey, val ? "1" : "0");
      else merged.set(urlKey, String(val));
    }
    // Overlay URL params on top so live-edit previews still work
    urlParams.forEach((v, k) => merged.set(k, v));
    return merged;
  }, [serverConfig]);
  const nicheKey = (params.get("n") || "general").toLowerCase();
  // Base niche from the built-in NICHES map. If the slug isn't recognized (e.g.
  // rep added a custom "landscaping" niche in the builder) we fall back to the
  // "general" preset and let ?nname / ?nsuffix override the display labels.
  const baseNiche = NICHES[nicheKey] || NICHES.general;
  const customNicheName = (params.get("nname") || "").trim();
  const customNicheSuffix = (params.get("nsuffix") || "").trim();
  const niche = (customNicheName || customNicheSuffix)
    ? { ...baseNiche, name: customNicheName || baseNiche.name, suffix: customNicheSuffix || baseNiche.suffix }
    : baseNiche;

  const company = params.get("co");
  const first = params.get("first");
  const expRaw = params.get("exp");
  const expFmt = formatExpiry(expRaw);
  const repName = (params.get("rep") || "").trim();
  const repPhoto = (params.get("pic") || "").trim();
  const repEmail = ((params.get("repemail") || "").trim() || "terry@skyeai.io").toLowerCase();
  const rescheduleUrl = (() => {
    const u = (params.get("resched") || "").trim();
    return /^https?:\/\//i.test(u) ? u : "";
  })();
  const loomUrl = (params.get("loom") || "").trim();
  const loomEmbed = getLoomEmbed(loomUrl);
  const planId = (params.get("pid") || "").trim();
  // Preview mode: when reps click "Preview" from /plan-builder we append `preview=1`
  // to skip analytics tracking so their own click-throughs don't inflate stats.
  const previewMode = params.get("preview") === "1";

  // Pricing version — v2 (current) = $5,000 + $79/qualified or $250/shown.
  // v1 (legacy) = $3,500 + $99/qualified or $250/shown. Old proposals with no
  // stored version default to v1 so they render at their original price. Rep
  // previews (URL-only, no server config) default to v2 (new offer).
  const pricingVersion = ((params.get("pv") || "v2").toLowerCase() === "v1") ? "v1" : "v2";
  const isLegacyPricing = pricingVersion === "v1";
  const setupFee = isLegacyPricing ? 3500 : 5000;
  const perShownFee = isLegacyPricing ? 99 : 79;   // per QUALIFIED appt
  const perShownAltFee = 250;                       // per SHOWN appt (unchanged in v2)

  // Payment button visibility — reps can hide either the full-pay or split-pay
  // button, but the URLs themselves are hardcoded (CHECKOUT_URL / CHECKOUT_SPLIT_URL).
  const showFull = params.get("showfull") !== "0";     // default: shown
  const showSplit = params.get("showsplit") !== "0";   // default: shown
  const payFullUrl = CHECKOUT_URL;
  const paySplitUrl = CHECKOUT_SPLIT_URL;

  const { markCtaClicked, markDemoCalled } = usePlanAnalytics(planId, process.env.REACT_APP_BACKEND_URL, previewMode);

  // Read numbers from URL params only — no interactive inputs on the prospect view
  // Only show the "Your Numbers" section when ALL three are provided by the rep.
  const rawJob = params.get("job");
  const rawAppts = params.get("appts");
  const rawClose = params.get("close");
  const hasNumbers = [rawJob, rawAppts, rawClose].every((v) => v !== null && v !== "" && Number(v) > 0);
  const avgJob = parseIntSafe(rawJob, 0);
  const appts = parseIntSafe(rawAppts, 0);
  const closeRate = parseIntSafe(rawClose, 0);

  const safeAppts = Math.max(0, appts);
  const safeJob = Math.max(0, avgJob);
  const safeClose = Math.min(100, Math.max(0, closeRate));

  const revenue = safeJob * safeAppts * (safeClose / 100);
  const cost = safeAppts * perShownFee;
  const kept = revenue - cost;
  const ratio = cost > 0 ? revenue / cost : 0;

  useEffect(() => {
    document.title = `Skye AI · Your Plan${company ? ` — ${company}` : ""}`;
    document.body.style.background = "#060610";
    // Load Manrope + IBM Plex Mono once
    if (!document.getElementById("plan-fonts")) {
      const link = document.createElement("link");
      link.id = "plan-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap";
      document.head.appendChild(link);
    }
  }, [company]);

  // Sticky mobile CTA: reveal once the hero section is scrolled past
  const [showStickyCta, setShowStickyCta] = useState(false);
  // Lazy-render testimonials past the first 6 — cuts initial DOM cost by ~85%
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  // Scroll hint: shown on mobile ONLY while the user is still near the top;
  // fades out once they start scrolling so it doesn't become clutter.
  const [showScrollHint, setShowScrollHint] = useState(true);
  useEffect(() => {
    const heroEl = document.querySelector('[data-track="hero"]');
    if (!heroEl) return;
    const onScroll = () => {
      const rect = heroEl.getBoundingClientRect();
      // Show sticky CTA once hero bottom is above the viewport
      setShowStickyCta(rect.bottom < 0);
      // Hide scroll hint after the user has scrolled ~180px (they got the idea)
      setShowScrollHint((window.scrollY || 0) < 180);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Team roster — the 5 real humans assigned to your account
  const team = [
    { role: { icon: Handshake,  title: "Success Manager" },  tasks: ["Your single point of contact", "Weekly check-ins & priority support", "Onboarding & strategy planning"], color: "#4AE3B5" },
    { role: { icon: Cpu,        title: "AI Engineer" },       tasks: ["Builds & deploys your AI agents", "AI tuned to your brand voice & objections", "Inbound & outbound conversation flows"], color: "#0A84FF" },
    { role: { icon: Settings,   title: "CRM Specialist" },    tasks: ["Configures pipelines & automations", "Integrates your tools into one system", "Calendar, forms & lead routing"], color: "#FB923C" },
    { role: { icon: LineChart,  title: "Growth Analyst" },    tasks: ["Monitors & improves conversion", "Monthly data-driven reports", "A/B testing & revenue optimization"], color: "#A78BFA" },
    { role: { icon: Megaphone,  title: "Marketing Manager" }, tasks: ["Runs your ads & campaigns", "Fresh creatives & copy monthly", "Reporting & revenue optimization"], color: "#F59E0B" },
  ];

  // Short-URL loading gate: if a pid is in the URL and we haven't loaded config yet
  // AND the URL doesn't carry the long-form params (i.e. it's a short client link),
  // show a small loader instead of rendering with fallback defaults.
  const urlHasLongParams = params.get("n") || params.get("setup") || params.get("co");
  const shortUrlLoading = !!planId && configLoading && !urlHasLongParams && !serverConfig;

  if (shortUrlLoading) {
    return (
      <div data-testid="plan-loading" style={{ background: "#060610", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Manrope', system-ui, sans-serif" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <img src="/skyeai_head.png" alt="Skye AI" style={{ width: 60, height: 60, filter: "drop-shadow(0 4px 14px rgba(244,114,182,0.45))", animation: "planLogoFloat 3s ease-in-out infinite" }} />
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>Loading your plan…</div>
        </div>
        <style>{`
          @keyframes planLogoFloat {
            0%, 100% { transform: translateY(0) rotate(-2deg); }
            50%      { transform: translateY(-4px) rotate(2deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div data-testid="plan-proposal-page" style={{ background: "#060610", color: "#fff", minHeight: "100vh", fontFamily: "'Manrope', system-ui, sans-serif", overflowX: "hidden" }}>
      {/* Ambient background glow orbs */}
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: -160, right: -120, width: 520, height: 520, borderRadius: "50%", background: "rgba(10,132,255,0.09)", filter: "blur(120px)" }} />
        <div style={{ position: "absolute", bottom: -180, left: -140, width: 480, height: 480, borderRadius: "50%", background: "rgba(167,139,250,0.07)", filter: "blur(120px)" }} />
        <div style={{ position: "absolute", top: "45%", left: "40%", width: 380, height: 380, borderRadius: "50%", background: "rgba(74,222,128,0.05)", filter: "blur(120px)" }} />
      </div>

      <main style={{ position: "relative", zIndex: 1, maxWidth: 1060, margin: "0 auto", padding: "56px 20px 100px" }}>

        {/* ═══════ PREVIEW MODE BADGE — shown when rep is previewing from /plan-builder ═══════ */}
        {previewMode && (
          <div data-testid="preview-badge" style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, background: "rgba(251,146,60,0.12)", border: "1px solid rgba(251,146,60,0.4)", color: "#FDBA74", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
              <Icon Cmp={Eye} size={13} color="#FDBA74" strokeWidth={2.4} />
              <span>Preview Mode · Analytics Paused</span>
            </div>
          </div>
        )}

        {/* ═══════ BRAND + REP LOCKUP — floating, no card, wordmark below the logo ═══════ */}
        <div data-testid="plan-brand-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 44, textAlign: "center" }}>
          {/* BIG logo with aurora glow */}
          <div style={{ position: "relative", display: "inline-flex", justifyContent: "center", alignItems: "center", marginBottom: 14 }}>
            {/* Aurora radial glow */}
            <span aria-hidden style={{ position: "absolute", width: 190, height: 190, borderRadius: "50%", background: "radial-gradient(circle at center, rgba(244,114,182,0.45) 0%, rgba(167,139,250,0.28) 40%, rgba(90,178,255,0.14) 65%, transparent 80%)", filter: "blur(18px)", animation: "planLogoPulse 3.6s ease-in-out infinite", pointerEvents: "none" }} />
            {/* Second, tighter glow ring */}
            <span aria-hidden style={{ position: "absolute", width: 110, height: 110, borderRadius: "50%", background: "radial-gradient(circle at center, rgba(255,255,255,0.08), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
            <img
              src="/skyeai_head.png"
              alt="Skye AI"
              data-testid="plan-brand-logo"
              draggable={false}
              loading="eager"
              fetchPriority="high"
              width={96}
              height={96}
              style={{ position: "relative", width: 96, height: 96, objectFit: "contain", filter: "drop-shadow(0 8px 26px rgba(244,114,182,0.55)) drop-shadow(0 2px 8px rgba(90,178,255,0.35))", animation: "planLogoFloat 5.5s ease-in-out infinite" }}
            />
          </div>

          {/* Gradient wordmark */}
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: "-0.03em", lineHeight: 1, background: "linear-gradient(90deg, #F472B6 0%, #A78BFA 45%, #5AB2FF 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", filter: "drop-shadow(0 2px 12px rgba(167,139,250,0.35))" }}>
            Skye AI
          </div>

          {/* Rep attribution — free-floating, no box */}
          {repName && (
            <div
              data-testid="rep-attribution"
              data-track="rep"
              style={{ marginTop: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}
            >
              {/* Ornamental separator: diamond flanked by gradient hairlines */}
              <div aria-hidden style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.7 }}>
                <span style={{ width: 46, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35))" }} />
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: "linear-gradient(135deg, #F472B6, #A78BFA)", boxShadow: "0 0 8px rgba(244,114,182,0.6)" }} />
                <span style={{ width: 46, height: 1, background: "linear-gradient(90deg, rgba(255,255,255,0.35), transparent)" }} />
              </div>

              {/* PREPARED FOR YOU BY */}
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, letterSpacing: 3.2, textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>
                Prepared for you by
              </div>

              {/* Rep photo + name — no box, just floating */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                {repPhoto ? (
                  <img
                    data-testid="rep-photo"
                    src={repPhoto}
                    alt={repName}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(10,132,255,0.55)", boxShadow: "0 0 14px rgba(10,132,255,0.4)" }}
                  />
                ) : (
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg, #A78BFA, #F472B6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#fff", boxShadow: "0 0 14px rgba(167,139,250,0.45)" }}>
                    {repName.trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <div data-testid="rep-name" style={{ fontFamily: "'Manrope', sans-serif", fontSize: 17, fontWeight: 800, color: "#fff", letterSpacing: "-0.015em" }}>
                  {repName}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ═══════ SLIDE 0 · HERO ═══════ */}
        <Reveal>
          <section data-track="hero" style={{ textAlign: "center", marginBottom: 88 }}>
            {company && (
              <div data-testid="prepared-for" style={{ display: "inline-block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.55)", padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)", marginBottom: 20 }}>
                Prepared for {company}
              </div>
            )}
            {first && (
              <div
                data-testid="personal-greeting"
                style={{
                  fontSize: "clamp(24px, 4.4vw, 40px)",
                  fontWeight: 800,
                  color: "rgba(255,255,255,0.9)",
                  fontFamily: "'Manrope', sans-serif",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  marginBottom: 14,
                }}
              >
                Hey{" "}
                <span
                  style={{
                    background: "linear-gradient(120deg, #FBBF24, #F472B6, #A78BFA)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {first}
                </span>{" "}
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    transformOrigin: "70% 70%",
                    animation: "planWave 2.2s ease-in-out infinite",
                  }}
                >
                  👋
                </span>
              </div>
            )}
            <h1 style={{ fontSize: "clamp(38px, 7vw, 74px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.03, margin: 0, fontFamily: "'Manrope', sans-serif" }}>
              Your{" "}
              <span style={{ background: "linear-gradient(120deg, #A78BFA, #F472B6, #FB923C)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                AI Revenue Engine
              </span>
              <span style={{ fontSize: "0.55em", verticalAlign: "super", color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>™</span>
            </h1>
            <div style={{ fontSize: 15, color: "#FBBF24", marginTop: 14, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 3, textTransform: "uppercase", fontWeight: 700 }}>
              {niche.suffix}
            </div>

            {/* Contractor-friendly scroll nudge — sends thinkers straight to the price */}
            <div style={{ marginTop: 30 }}>
              <InlineCTA testid="cta-inline-hero" label="Click here to get started" />
            </div>
          </section>
        </Reveal>

        {/* ═══════ WHAT WE DO — 4-STEP PROMISE (headliner) ═══════ */}
        <Reveal delay={40}>
          <section data-track="how" style={{ marginBottom: 88 }}>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <Eyebrow color="#F472B6">The Whole Machine</Eyebrow>
              <h2 style={{ fontSize: "clamp(28px, 4.2vw, 46px)", fontWeight: 800, letterSpacing: "-0.025em", margin: 0, fontFamily: "'Manrope', sans-serif", lineHeight: 1.08 }}>
                We get you the leads. We book them.<br />
                <span style={{ background: "linear-gradient(120deg, #4ADE80, #22D3EE, #A78BFA)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  We make them show — and get you 5-star reviews.
                </span>
              </h2>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", maxWidth: 620, margin: "16px auto 0", lineHeight: 1.55 }}>
                One flat system for the whole customer journey. You just show up to jobs — we run everything else.
              </p>
            </div>

            <div className="plan-steps-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              {[
                { n: "01", color: "#F59E0B", Icn: Target,        title: "We get you the leads",             desc: "Google & Meta ads dialed in daily · exclusive leads · one company per service area" },
                { n: "02", color: "#0A84FF", Icn: Phone,         title: "We book the appointments",         desc: "AI answers 24/7 across text, chat, IG & DMs · qualifies · drops onto your calendar" },
                { n: "03", color: "#4ADE80", Icn: CheckCircle2,  title: "We make sure they show",           desc: "Multi-touch reminder sequence protects your show rate · you only pay when they show up" },
                { n: "04", color: "#FBBF24", Icn: Star,          title: "We collect the 5-star reviews",    desc: "After every job, our AI review manager captures the star and story · your Google profile compounds" },
              ].map((s, i) => (
                <div
                  key={i}
                  data-testid={`step-${i + 1}`}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    borderRadius: 18,
                    padding: "22px 20px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: -20,
                      right: -20,
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${s.color}22, transparent 70%)`,
                      filter: "blur(24px)",
                      pointerEvents: "none",
                    }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: 2.5, color: s.color, fontWeight: 700 }}>
                      {s.n}
                    </div>
                    <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${s.color}55, transparent)` }} />
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: `linear-gradient(135deg, ${s.color}44, ${s.color}18)`,
                        border: `1px solid ${s.color}66`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon Cmp={s.Icn} size={20} color={s.color} strokeWidth={2.2} />
                    </div>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", color: "#fff", fontFamily: "'Manrope', sans-serif", lineHeight: 1.2 }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.55 }}>
                    {s.desc}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom cinch — payoff */}
            <div style={{ marginTop: 24, textAlign: "center" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "12px 22px", borderRadius: 999, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.3)", fontSize: 13.5, color: "#fff", fontWeight: 600, fontFamily: "'Manrope', sans-serif" }}>
                <span style={{ color: "#4ADE80", fontWeight: 800 }}>→</span>
                <span>Every new customer feeds the next one. Your business compounds.</span>
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 22 }}>
              <InlineCTA testid="cta-inline-how" label="Yep, I want this — click here" />
            </div>
          </section>
        </Reveal>

        {/* ═══════ LOOM VIDEO (if provided by rep) ═══════ */}
        {loomEmbed && (
          <Reveal delay={60}>
            <section data-testid="loom-section" data-track="loom" style={{ marginBottom: 88 }}>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Eyebrow color="#F472B6">A Personal Note{repName ? ` From ${repName.split(" ")[0]}` : ""}</Eyebrow>
                <h2 style={{ fontSize: "clamp(22px, 2.8vw, 32px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                  Watch this <span style={{ background: "linear-gradient(120deg, #F472B6, #FB923C)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>2-minute walkthrough</span>
                </h2>
              </div>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 20, overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 32px rgba(244,114,182,0.15)" }}>
                <iframe
                  data-testid="loom-embed"
                  src={loomEmbed}
                  title="Personal video from your rep"
                  frameBorder="0"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                />
              </div>
            </section>
          </Reveal>
        )}

        {/* ═══════ SLIDE 1 · WHAT YOU GET (Pillars) ═══════ */}
        <Reveal delay={80}>
          <section data-track="pillars" style={{ marginBottom: 88 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <Eyebrow color="#0A84FF">What You Actually Get</Eyebrow>
              <h2 style={{ fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Three engines. <span style={{ background: "linear-gradient(120deg, #5AB2FF, #0A84FF)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>One outcome.</span>
              </h2>
            </div>

            <div className="plan-pillars-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }}>
              {[
                {
                  color: "#F59E0B",
                  eyebrow: "Card 1",
                  title: "The Marketing Engine",
                  subtitle: "Your marketing — researched, built and launched for you",
                  items: [
                    "Market research deep-dive — every competitor in your area studied",
                    "Google Business Profile — set up and fully optimized",
                    "Your website built — designed to turn clicks into calls",
                    "First 20 creatives made — images, videos and spokesperson ads",
                    "Campaigns launched — Google and Meta, your service area only",
                    "Optimized every day — budgets tuned, retargeting switched on",
                  ],
                },
                {
                  color: "#0A84FF",
                  eyebrow: "Card 2",
                  title: "The Messaging AI Engine",
                  subtitle: "Your AI goes live — wired and answering in seconds",
                  items: [
                    "AI trained on your business — services, pricing and brand voice",
                    "Dedicated texting line set up — your AI's own business number",
                    "AI calls, inbound and outbound — answers, qualifies and books",
                    "Every channel wired in — website chat, Instagram and Facebook DMs",
                    "Smart follow-up loaded — 5+ touches until they book",
                    "AI review manager set up — 5 stars collected after every job",
                    "Old and cold leads reactivated — revenue pulled from your dead list",
                  ],
                },
                {
                  color: "#4ADE80",
                  eyebrow: "Card 3",
                  title: "The AI Client Hub",
                  subtitle: "Your command center — nothing falls through",
                  items: [
                    "CRM and pipeline built — first message to closed deal",
                    "Calendar connected — estimates book straight in, auto-confirmed",
                    "Reminder calls and texts armed — AI protects your show rate",
                    "One inbox for everything — texts, calls and bookings in one place",
                    "Weekly numbers reported — your Growth Analyst shows what's working",
                    "Tuned daily by real people — your team improves it daily",
                  ],
                },
              ].map((p, i) => (
                <div key={i} data-testid={`pillar-${i + 1}`} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: 24, position: "relative", overflow: "hidden" }}>
                  <div aria-hidden style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: `radial-gradient(circle, ${p.color}22, transparent 70%)`, filter: "blur(30px)", pointerEvents: "none" }} />
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2.4, textTransform: "uppercase", color: p.color, fontWeight: 700, marginBottom: 8 }}>{p.eyebrow}</div>
                  <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-0.01em", marginBottom: 4, fontFamily: "'Manrope', sans-serif" }}>{p.title}</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 16 }}>{p.subtitle}</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                    {p.items.map((it, ii) => (
                      <li key={ii} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>
                        <span aria-hidden style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 999, background: `${p.color}22`, border: `1px solid ${p.color}66`, color: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, marginTop: 1 }}>✓</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ═══════ SLIDE 3 · TEAM ═══════ */}
        <Reveal delay={80}>
          <section data-track="team" style={{ marginBottom: 88 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <Eyebrow color="#4AE3B5">Your Real Human Team</Eyebrow>
              <h2 style={{ fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Five real humans, <span style={{ background: "linear-gradient(120deg, #4AE3B5, #22D3EE)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>assigned to you.</span>
              </h2>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", maxWidth: 560, margin: "10px auto 0", lineHeight: 1.55 }}>
                Not a chatbot pretending. Real people managing your engine every day — tuning it, reporting on it, and answering when you need them.
              </p>
            </div>

            {/* Big stats strip — social proof up top */}
            <div
              data-testid="team-stats"
              className="plan-team-stats"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginBottom: 24,
                textAlign: "center",
              }}
            >
              {[
                { value: "300+", label: "Businesses Served", gradient: "linear-gradient(120deg, #5AB2FF, #0A84FF)" },
                { value: "3 Yrs", label: "In Business",        gradient: "linear-gradient(120deg, #A78BFA, #F472B6)" },
                { value: "5",     label: "Experts On Your Team", gradient: "linear-gradient(120deg, #4ADE80, #22D3EE)" },
              ].map((s, i) => (
                <div
                  key={i}
                  data-testid={`team-stat-${i + 1}`}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 16,
                    padding: "18px 10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "clamp(30px, 5vw, 46px)",
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      fontFamily: "'Manrope', sans-serif",
                      lineHeight: 1,
                      background: s.gradient,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {s.value}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: 1.6,
                      color: "rgba(255,255,255,0.55)",
                      marginTop: 8,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontWeight: 600,
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="plan-team-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              {team.map((m, i) => (
                <TeamCard key={i} role={m.role} tasks={m.tasks} color={m.color} />
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 26 }}>
              <InlineCTA testid="cta-inline-team" label="Put this team to work — click here" />
            </div>
          </section>
        </Reveal>

        {/* ═══════ YOUR NUMBERS (Static — from rep-supplied URL params) ═══════ */}
        {hasNumbers && (
        <Reveal delay={80}>
          <section id="numbers" data-track="numbers" style={{ marginBottom: 88 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <Eyebrow color="#4ADE80">Your Numbers</Eyebrow>
              <h2 style={{ fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                What this looks like <span style={{ background: "linear-gradient(120deg, #4ADE80, #22D3EE)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>for {company || "your business"}.</span>
              </h2>
            </div>

            {/* Three input display cards */}
            <div className="plan-inputs-static" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 22 }}>
              <StaticStat label="Average Job Value" value={money(safeJob)} testid="static-job" />
              <StaticStat label="Appointments / mo" value={num(safeAppts)} testid="static-appts" />
              <StaticStat label="Your Close Rate" value={safeClose + "%"} testid="static-close" />
            </div>

            {/* Big result */}
            <div style={{ background: "linear-gradient(180deg, rgba(74,222,128,0.11), rgba(74,222,128,0.02))", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 24, padding: "36px 26px", textAlign: "center", position: "relative", overflow: "hidden" }}>
              <div aria-hidden style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 30%, rgba(74,222,128,0.15), transparent 60%)", pointerEvents: "none" }} />
              <div style={{ position: "relative" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: 3, color: "rgba(74,222,128,0.9)", textTransform: "uppercase", fontWeight: 700, marginBottom: 14 }}>
                  Revenue From Those Appointments
                </div>
                <div data-testid="static-revenue" style={{ fontSize: "clamp(52px, 12vw, 96px)", fontWeight: 800, color: "#4ADE80", letterSpacing: "-0.03em", lineHeight: 1, fontFamily: "'Manrope', sans-serif", textShadow: "0 0 40px rgba(74,222,128,0.4)" }}>
                  {money(revenue)}
                </div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginTop: 16, fontWeight: 500 }}>
                  Your cost: <span style={{ color: "#F87171", fontWeight: 700 }}>{money(cost)}</span>/mo — you keep <span style={{ color: "#4ADE80", fontWeight: 800 }}>{money(kept)}</span>
                </div>
              </div>
            </div>

            {/* Cost vs Return */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <div style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 16, padding: 18, textAlign: "center" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#F87171", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Your Cost</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#F87171", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em" }}>
                  {money(cost)}<span style={{ fontSize: 13, opacity: 0.6, fontWeight: 500 }}> / mo</span>
                </div>
              </div>
              <div style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 16, padding: 18, textAlign: "center" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#4ADE80", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Your Return</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#4ADE80", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em" }}>
                  {money(revenue)}<span style={{ fontSize: 13, opacity: 0.6, fontWeight: 500 }}> / mo</span>
                </div>
              </div>
            </div>

            {/* ROI ratio pill */}
            {ratio >= 2 && (
              <div style={{ marginTop: 22, textAlign: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 22px", borderRadius: 999, background: "linear-gradient(135deg, rgba(15,200,240,0.09), rgba(10,132,255,0.06))", border: "1px solid rgba(15,200,240,0.3)", fontSize: 14.5, color: "#fff", fontWeight: 600 }}>
                  For every $1 you spend, you get back{" "}
                  <span style={{ color: "#4ADE80", fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18 }}>${ratio.toFixed(1)}</span>
                </div>
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: 22 }}>
              <InlineCTA testid="cta-inline-numbers" label="These numbers look good? Click here" />
            </div>
          </section>
        </Reveal>
        )}

        {/* ═══════ HEAR IT LIVE — demo phone number, right before pricing ═══════ */}
        <Reveal delay={60}>
          <section data-track="demo" style={{ marginBottom: 56 }}>
            <a
              href="tel:+19548721460"
              data-testid="demo-call-card"
              onClick={markDemoCalled}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "26px 20px",
                borderRadius: 20,
                background: "linear-gradient(135deg, rgba(10,132,255,0.08), rgba(74,222,128,0.06) 55%, rgba(10,132,255,0.08))",
                border: "1px solid rgba(74,222,128,0.32)",
                boxShadow: "0 10px 30px -8px rgba(74,222,128,0.24), inset 0 1px 0 rgba(255,255,255,0.06)",
                textDecoration: "none",
                color: "#fff",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.borderColor = "rgba(74,222,128,0.5)";
                e.currentTarget.style.boxShadow = "0 14px 36px -8px rgba(74,222,128,0.38), inset 0 1px 0 rgba(255,255,255,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(74,222,128,0.32)";
                e.currentTarget.style.boxShadow = "0 10px 30px -8px rgba(74,222,128,0.24), inset 0 1px 0 rgba(255,255,255,0.06)";
              }}
            >
              {/* Ambient glow orb */}
              <div aria-hidden style={{ position: "absolute", top: "-45%", left: "50%", width: 220, height: 220, transform: "translateX(-50%)", borderRadius: "50%", background: "radial-gradient(circle at center, rgba(74,222,128,0.22), transparent 70%)", filter: "blur(28px)", pointerEvents: "none" }} />

              {/* Pulsing phone icon — mid-size */}
              <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                <span aria-hidden style={{ position: "absolute", width: 58, height: 58, borderRadius: "50%", background: "rgba(74,222,128,0.18)", animation: "demoCallPulse 2.2s ease-out infinite" }} />
                <span aria-hidden style={{ position: "absolute", width: 58, height: 58, borderRadius: "50%", background: "rgba(74,222,128,0.14)", animation: "demoCallPulse 2.2s ease-out infinite .7s" }} />
                <div style={{ position: "relative", width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #4ADE80, #22C55E)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 18px rgba(74,222,128,0.45)" }}>
                  <Icon Cmp={Phone} size={18} color="#052E16" strokeWidth={2.8} />
                </div>
              </div>

              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2.8, textTransform: "uppercase", color: "#4ADE80", fontWeight: 700, marginBottom: 8 }}>
                Hear it live · 24/7
              </div>

              <div style={{ fontSize: "clamp(17px, 2.2vw, 22px)", fontWeight: 800, letterSpacing: "-0.015em", margin: "0 0 14px", fontFamily: "'Manrope', sans-serif", lineHeight: 1.2 }}>
                Want to hear the AI work?{" "}
                <span style={{ background: "linear-gradient(120deg, #4ADE80, #22D3EE, #5AB2FF)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Tap the number to call it now
                </span>
              </div>

              <div
                data-testid="demo-phone-number"
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 20px",
                  borderRadius: 999,
                  background: "linear-gradient(135deg, #4ADE80 0%, #22C55E 100%)",
                  color: "#052E16",
                  marginBottom: 10,
                  boxShadow: "0 6px 20px -4px rgba(74,222,128,0.5), 0 0 0 2px rgba(74,222,128,0.3)",
                  animation: "demoNumberBreathe 2.4s ease-in-out infinite",
                }}
              >
                {/* Floating "TAP TO CALL" tag */}
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: -10,
                    right: -8,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#060610",
                    border: "1px solid rgba(74,222,128,0.7)",
                    color: "#4ADE80",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 8.5,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    transform: "rotate(4deg)",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.4)",
                  }}
                >
                  ← Tap to call
                </span>

                <Icon Cmp={Phone} size={17} color="#052E16" strokeWidth={2.8} />
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 17, fontWeight: 800, letterSpacing: 0.4 }}>+1 (954) 872-1460</span>
                <Icon Cmp={ArrowRight} size={16} color="#052E16" strokeWidth={2.8} />
              </div>

              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", maxWidth: 440, lineHeight: 1.5 }}>
                Talk to our AI like a real prospect — book yourself an &ldquo;appointment,&rdquo; ask it anything, hang up when you&rsquo;re convinced.
              </div>
            </a>
          </section>
        </Reveal>

        {/* ═══════ SLIDE 5 · PRICING ═══════ */}
        <Reveal delay={80}>
          <section id="pricing" data-track="pricing" style={{ marginBottom: 72, scrollMarginTop: 24 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <Eyebrow color="#0A84FF">Investment</Eyebrow>
              <h2 style={{ fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Start your <span style={{ background: "linear-gradient(120deg, #A78BFA, #F472B6, #FB923C)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI Revenue Engine™</span>
              </h2>
            </div>

            <div style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 24, padding: "36px 28px", textAlign: "center", position: "relative", overflow: "hidden" }}>
              <div aria-hidden style={{ position: "absolute", top: -50, left: "50%", transform: "translateX(-50%)", width: 300, height: 200, background: "radial-gradient(ellipse, rgba(167,139,250,0.15), transparent 70%)", filter: "blur(40px)", pointerEvents: "none" }} />

              <div style={{ position: "relative" }}>
                {/* Performance flag */}
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20, padding: "6px 14px", borderRadius: 999, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.35)", fontSize: 11, color: "#FBBF24", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1.2, textTransform: "uppercase" }}>
                  <Icon Cmp={Zap} size={13} color="#FBBF24" /> Performance-Based Pricing
                </div>

                <div style={{ fontSize: "clamp(42px, 8vw, 68px)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.05, fontFamily: "'Manrope', sans-serif" }}>
                  {money(setupFee)}
                </div>
                <div style={{ fontSize: 15, color: "rgba(255,255,255,0.7)", marginTop: 4, fontWeight: 500 }}>
                  one-time setup · no monthly fee — <span style={{ color: "#fff", fontWeight: 700 }}>ever</span>
                </div>

                {/* Dual pricing — prospect picks their model. Legacy v1 plans stay at $99/$250. */}
                <div style={{ display: "inline-flex", alignItems: "stretch", gap: 14, marginTop: 22, flexWrap: "nowrap", justifyContent: "center" }}>
                  <div data-testid="price-qualified" style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "12px 24px", borderRadius: 100, background: "rgba(74,222,128,0.09)", border: "1px solid rgba(74,222,128,0.4)", textAlign: "center" }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: "#4ADE80", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em", lineHeight: 1 }}>${perShownFee}</span>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, textTransform: "uppercase", lineHeight: 1.2 }}>per qualified appt</span>
                  </div>
                  <span style={{ alignSelf: "center", fontSize: 15, fontWeight: 800, letterSpacing: 1.5, color: "#FBBF24", padding: "9px 14px", borderRadius: 100, border: "2px solid rgba(251,191,36,0.55)", background: "rgba(251,191,36,0.14)", fontFamily: "'Space Grotesk', sans-serif", boxShadow: "0 0 22px rgba(251,191,36,0.28), inset 0 0 12px rgba(251,191,36,0.08)", textShadow: "0 0 12px rgba(251,191,36,0.5)", flexShrink: 0 }}>OR</span>
                  <div data-testid="price-shown" style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "12px 24px", borderRadius: 100, background: "rgba(10,132,255,0.08)", border: "1px solid rgba(10,132,255,0.35)", textAlign: "center" }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: "#5AB2FF", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em", lineHeight: 1 }}>${perShownAltFee}</span>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, textTransform: "uppercase", lineHeight: 1.2 }}>per shown appt</span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  👉 You pick ONE model
                </div>

                <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", marginTop: 16, maxWidth: 500, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5, fontStyle: "italic", display: "flex", alignItems: "flex-start", gap: 6, justifyContent: "center" }}>
                  <Icon Cmp={Zap} size={14} color="#FBBF24" style={{ marginTop: 2 }} />
                  <span>100% performance-based. You only pay when it works.</span>
                </div>

                {/* ═══ Integrated CTA — the close moment ═══ */}
                <div data-track="cta" style={{ marginTop: 32 }}>
                  {expFmt && (
                    <div data-testid="expiry-line" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "#FBBF24", marginBottom: 14, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700, padding: "5px 12px", borderRadius: 999, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)" }}>
                      <Icon Cmp={Clock} size={12} color="#FBBF24" /> Pricing holds through {expFmt}
                    </div>
                  )}

                  {isLegacyPricing ? (
                    /* Legacy proposals — payment links point at retired Stripe products.
                       Direct the prospect to email so we can process manually. */
                    <div data-testid="legacy-email-cta">
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginBottom: 14, lineHeight: 1.55, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
                        This proposal was sent under our earlier pricing — the numbers above still hold for you. To lock it in, shoot us a quick email and we&apos;ll send over a payment link that matches.
                      </div>
                      <a
                        href="mailto:info@skyeai.io?subject=Locking%20in%20my%20AI%20Revenue%20Engine%20proposal"
                        data-testid="cta-legacy-email"
                        onClick={markCtaClicked}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "18px 32px",
                          borderRadius: 999,
                          background: "linear-gradient(135deg, #A78BFA, #F472B6, #FB923C)",
                          color: "#fff",
                          textDecoration: "none",
                          fontSize: 16,
                          fontWeight: 800,
                          letterSpacing: "-0.01em",
                          fontFamily: "'Manrope', sans-serif",
                          boxShadow: "0 14px 44px -8px rgba(167,139,250,0.55), 0 0 40px rgba(244,114,182,0.3)",
                          transition: "transform .25s ease, box-shadow .25s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0) scale(1)";
                        }}
                      >
                        <Icon Cmp={Zap} size={17} color="#fff" strokeWidth={2.4} />
                        <span>Email us to get started</span>
                        <Icon Cmp={ArrowRight} size={17} color="#fff" strokeWidth={2.4} />
                      </a>
                      <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.5)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.5 }}>
                        info@skyeai.io
                      </div>
                    </div>
                  ) : (
                    <>
                      {(showFull || (!showFull && !showSplit)) && (
                        <div>
                          <a
                            href={payFullUrl}
                            data-testid="cta-primary"
                            onClick={markCtaClicked}
                            target={payFullUrl.startsWith("http") && !payFullUrl.includes(window.location.host) ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "20px 36px",
                              borderRadius: 999,
                              background: "linear-gradient(135deg, #A78BFA, #F472B6, #FB923C)",
                              color: "#fff",
                              textDecoration: "none",
                              fontSize: 17,
                              fontWeight: 800,
                              letterSpacing: "-0.01em",
                              fontFamily: "'Manrope', sans-serif",
                              boxShadow: "0 14px 44px -8px rgba(167,139,250,0.55), 0 0 40px rgba(244,114,182,0.3)",
                              transition: "transform .25s ease, box-shadow .25s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                              e.currentTarget.style.boxShadow = "0 20px 56px -8px rgba(167,139,250,0.65), 0 0 48px rgba(244,114,182,0.4)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0) scale(1)";
                              e.currentTarget.style.boxShadow = "0 14px 44px -8px rgba(167,139,250,0.55), 0 0 40px rgba(244,114,182,0.3)";
                            }}
                          >
                            <Icon Cmp={Zap} size={18} color="#fff" strokeWidth={2.4} />
                            <span>Get started now</span>
                            <Icon Cmp={ArrowRight} size={18} color="#fff" strokeWidth={2.4} />
                          </a>
                        </div>
                      )}
                      {showSplit && (
                        <div style={{ marginTop: 14 }}>
                          <a
                            href={paySplitUrl}
                            data-testid="cta-split"
                            onClick={markCtaClicked}
                            target={paySplitUrl.startsWith("http") && !paySplitUrl.includes(window.location.host) ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            style={{ fontSize: 13.5, color: "rgba(255,255,255,0.65)", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)", textUnderlineOffset: 4 }}
                          >
                            Prefer to split it into 2 payments?
                          </a>
                        </div>
                      )}
                    </>
                  )}
                  {rescheduleUrl && (
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed rgba(255,255,255,0.1)" }}>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 10, fontFamily: "'Manrope', sans-serif" }}>
                        Ready to book a follow-up?
                      </div>
                      <a
                        href={rescheduleUrl}
                        data-testid="cta-reschedule"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 999, background: "rgba(90,178,255,0.10)", border: "1px solid rgba(90,178,255,0.35)", color: "#5AB2FF", textDecoration: "none", fontSize: 13.5, fontWeight: 800, fontFamily: "'Manrope', sans-serif" }}
                      >
                        <Icon Cmp={Clock} size={14} color="#5AB2FF" strokeWidth={2.4} />
                        Book a follow-up call
                      </a>
                    </div>
                  )}
                  <div style={{ marginTop: 16, fontSize: 11, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1.2, textTransform: "uppercase" }}>
                    Trusted by 300+ home-service owners
                  </div>
                </div>

                {/* Double Guarantee */}
                <div style={{ marginTop: 32, padding: "22px 22px", background: "linear-gradient(180deg, rgba(74,222,128,0.08), rgba(74,222,128,0.02))", border: "1px solid rgba(74,222,128,0.28)", borderRadius: 18, textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, justifyContent: "center" }}>
                    <Icon Cmp={ShieldCheck} size={22} color="#4ADE80" strokeWidth={2.2} />
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 2.2, color: "#4ADE80", textTransform: "uppercase", fontWeight: 700 }}>Double Guarantee</span>
                  </div>
                  {[
                    { t: "Live in 30 days", d: "Your engine is built, launched and running." },
                    { t: "30 exclusive booked estimates in 90 days", d: "Or your full setup fee back — zero risk." },
                    { t: "You never pay for a no-show", d: "You only pay when a homeowner is standing in your driveway." },
                  ].map((g, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderBottom: i < 2 ? "1px solid rgba(74,222,128,0.12)" : "none" }}>
                      <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, background: "rgba(74,222,128,0.2)", border: "1px solid rgba(74,222,128,0.5)", color: "#4ADE80", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, marginTop: 2 }}>✓</span>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif" }}>{g.t}</div>
                        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", marginTop: 2, lineHeight: 1.5 }}>{g.d}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tags */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 22 }}>
                  {[
                    { text: "Unlimited AI conversations", color: "rgba(255,255,255,0.5)", Icn: CheckCircle2 },
                    { text: "No contracts",               color: "rgba(255,255,255,0.5)", Icn: CheckCircle2 },
                    { text: "One company per area",       color: "#FBBF24",               Icn: MapPin },
                  ].map((c, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: c.color, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", padding: "6px 12px", borderRadius: 100, letterSpacing: 0.4 }}>
                      <Icon Cmp={c.Icn} size={12} color={c.color} strokeWidth={2.4} />
                      {c.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* ═══════ TESTIMONIALS — Trustpilot (last section, final trust boost) ═══════ */}
        <Reveal delay={80}>
          <section data-testid="testimonials-section" data-track="testimonials" style={{ marginBottom: 72 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <Eyebrow color="#00b67a">
                <TrustpilotWordmark inline />
                <span style={{ marginLeft: 6 }}>Verified Reviews</span>
              </Eyebrow>
              <h2 style={{ fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Rated <span style={{ color: "#00b67a" }}>{TRUSTPILOT_SUMMARY.label}</span> on Trustpilot.
              </h2>
              <div data-testid="trustpilot-summary" style={{ display: "inline-flex", alignItems: "center", gap: 12, marginTop: 18, padding: "10px 18px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, flexWrap: "wrap", justifyContent: "center" }}>
                <TrustpilotStars rating={TRUSTPILOT_SUMMARY.score} size={20} />
                <span style={{ fontSize: 15, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif" }}>{TRUSTPILOT_SUMMARY.score}</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>· 100% 5-star reviews</span>
              </div>
            </div>

            <div className="plan-testi-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              {(showAllTestimonials ? TESTIMONIALS : TESTIMONIALS.slice(0, 6)).map((t, i) => (
                <TrustpilotReviewCard key={i} review={t} idx={i} />
              ))}
              {!showAllTestimonials && (
                <button
                  onClick={() => setShowAllTestimonials(true)}
                  data-testid="testimonials-show-all"
                  style={{ marginTop: 4, padding: "12px 24px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.03)", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif", justifySelf: "center", alignSelf: "center", gridColumn: "1 / -1" }}
                >
                  Show all {TESTIMONIALS.length} reviews ↓
                </button>
              )}
            </div>

            {/* Final scroll-to-pricing nudge after testimonials */}
            <div style={{ textAlign: "center", marginTop: 28 }}>
              <InlineCTA onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" })} testid="cta-inline-final" label="Ready? Click here to get started" />
            </div>
          </section>
        </Reveal>

        {/* Small email footer */}
        <div style={{ textAlign: "center", padding: "8px 20px 40px", fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1.2, textTransform: "uppercase" }}>
          Questions? →{" "}
          <a data-testid="rep-email-link" href={`mailto:${repEmail}`} style={{ color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>
            {repEmail}
          </a>
        </div>
      </main>

      {/* ═══════ MOBILE SCROLL HINT — animated indicator on the right edge ═══════ */}
      <div
        data-testid="scroll-hint"
        className="plan-scroll-hint"
        aria-hidden={!showScrollHint}
        onClick={() => window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" })}
        style={{
          position: "fixed",
          right: 8,
          top: "50%",
          transform: `translateY(-50%) ${showScrollHint ? "translateX(0)" : "translateX(80px)"}`,
          opacity: showScrollHint ? 1 : 0,
          transition: "transform .45s cubic-bezier(.22,1,.36,1), opacity .35s ease",
          zIndex: 45,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "12px 6px 8px",
          borderRadius: 999,
          background: "linear-gradient(180deg, rgba(167,139,250,0.16), rgba(244,114,182,0.10))",
          border: "1px solid rgba(167,139,250,0.32)",
          boxShadow: "0 6px 18px rgba(167,139,250,0.22), 0 0 22px rgba(244,114,182,0.12)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        {/* Traveling dot inside a thin track */}
        <div aria-hidden style={{ position: "relative", width: 2, height: 44, background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.22), rgba(255,255,255,0.05))", borderRadius: 999, overflow: "hidden" }}>
          <span style={{ position: "absolute", left: -2, top: 0, width: 6, height: 6, borderRadius: "50%", background: "linear-gradient(135deg, #F472B6, #A78BFA)", boxShadow: "0 0 10px rgba(244,114,182,0.8)", animation: "scrollHintDot 1.8s ease-in-out infinite" }} />
        </div>

        {/* Vertical SCROLL text */}
        <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>
          Scroll
        </div>

        {/* Bouncing chevron */}
        <span aria-hidden style={{ display: "inline-flex", animation: "scrollHintBounce 1.4s ease-in-out infinite" }}>
          <Icon Cmp={ChevronDown} size={14} color="#F472B6" strokeWidth={2.8} />
        </span>
      </div>

      {/* ═══════ STICKY MOBILE CTA — visible on mobile only, after hero is scrolled past ═══════ */}
      <div
        data-testid="sticky-mobile-cta"
        className="plan-sticky-cta"
        aria-hidden={!showStickyCta}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          padding: "10px 14px calc(10px + env(safe-area-inset-bottom, 0px))",
          background: "linear-gradient(180deg, rgba(6,6,16,0.75) 0%, rgba(6,6,16,0.98) 60%)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          transform: showStickyCta ? "translateY(0)" : "translateY(110%)",
          opacity: showStickyCta ? 1 : 0,
          pointerEvents: showStickyCta ? "auto" : "none",
          transition: "transform .35s cubic-bezier(.22,1,.36,1), opacity .3s ease",
        }}
      >
        <a
          href="#pricing"
          data-testid="sticky-mobile-cta-btn"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("pricing");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: "100%",
            padding: "15px 22px",
            borderRadius: 999,
            background: "linear-gradient(135deg, #4ADE80 0%, #22C55E 100%)",
            color: "#031510",
            textDecoration: "none",
            fontSize: 15,
            fontWeight: 800,
            fontFamily: "'Manrope', sans-serif",
            letterSpacing: "-0.005em",
            boxShadow: "0 8px 22px rgba(74,222,128,0.35), 0 2px 6px rgba(0,0,0,0.4)",
          }}
        >
          <span>Click here to get started</span>
          <Icon Cmp={ArrowRight} size={17} color="#031510" strokeWidth={2.8} />
        </a>
      </div>

      {/* Desktop grids */}
      <style>{`
        @keyframes planLogoFloat {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50%      { transform: translateY(-3px) rotate(2deg); }
        }
        @keyframes planLogoPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.95; transform: scale(1.15); }
        }
        @keyframes scrollHintDot {
          0%   { top: -6px;  opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes scrollHintBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(3px); }
        }
        @keyframes demoCallPulse {
          0%   { transform: scale(0.7); opacity: 0.85; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes demoNumberBreathe {
          0%, 100% { transform: scale(1);    box-shadow: 0 10px 30px -6px rgba(74,222,128,0.55), 0 0 0 2px rgba(74,222,128,0.35), 0 0 40px rgba(74,222,128,0.25); }
          50%      { transform: scale(1.035); box-shadow: 0 14px 40px -6px rgba(74,222,128,0.75), 0 0 0 2px rgba(74,222,128,0.5),  0 0 56px rgba(74,222,128,0.42); }
        }
        @keyframes planWave {
          0%, 60%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(-14deg); }
          20% { transform: rotate(14deg); }
          30% { transform: rotate(-10deg); }
          40% { transform: rotate(10deg); }
          50% { transform: rotate(0deg); }
        }
        @media (max-width: 380px) {
          .plan-team-stats > div { padding: 14px 6px !important; }
        }
        @media (min-width: 900px) {
          .plan-pillars-grid { grid-template-columns: 1fr 1fr 1fr !important; }
          .plan-team-grid { grid-template-columns: repeat(5, 1fr) !important; }
          .plan-inputs-static { grid-template-columns: 1fr 1fr 1fr !important; }
          .plan-testi-grid { grid-template-columns: 1fr 1fr !important; }
          .plan-steps-grid { grid-template-columns: 1fr 1fr 1fr 1fr !important; }
        }
        @media (min-width: 768px) {
          .plan-sticky-cta { display: none !important; }
          .plan-scroll-hint { display: none !important; }
        }
        @media (min-width: 600px) and (max-width: 899px) {
          .plan-steps-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ── Static stat card (replaces the interactive input) ──
function StaticStat({ label, value, testid }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: "16px 18px", textAlign: "left" }}>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
        {label}
      </div>
      <div data-testid={testid} style={{ fontSize: 28, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em", lineHeight: 1 }}>
        {value}
      </div>
    </div>
  );
}
